import Axhelper from '@/lib/Axhelper'

class OsobaService {
	constructor() {
		this.baseUrl = window.RUNTIME_CONFIG.API_URL
		this.endpoints = {
			osobaAll: '/api/osoba/filters',
			osobaDetails: '/api/osoba/details',
			osobaSearch: '/api/osoba/filters',
			osobaCreate: '/api/osoba',
			osobaUpdate: '/api/osoba',
			osobaDelete: '/api/osoba',
		}
	}

	async getAll(options = {}) {
		try {
			const {
				limit = 50,
				sortColumn = '_id',
				sortDirection = 'desc',
			} = options

			const params = new URLSearchParams({
				sortcol: sortColumn,
				sortdir: sortDirection,
				limit: String(limit),
			})
			const url = `${this.endpoints.osobaAll}?${params.toString()}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: [],
					error: response.error,
					totalCount: 0,
					hasMore: false,
				}
			}

			const totalCount = response.headers?.['x-total-count']
				? parseInt(response.headers['x-total-count'], 10)
				: response.data?.length || 0

			return {
				data: response.data || [],
				error: null,
				totalCount,
				hasMore: (response.data?.length || 0) < totalCount,
				headers: response.headers,
			}
		} catch (error) {
			console.error('OsobaService.getAll error:', error)
			return {
				data: [],
				error: error.message || 'Failed to fetch osoby',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async getByPlacowkaId(placowkaId) {
		try {
			if (!placowkaId) {
				return {
					data: [],
					error: 'Invalid placowka ID provided',
				}
			}

			const url = `${this.endpoints.osobaSearch}?id_placowka=${encodeURIComponent(placowkaId)}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: [],
					error: response.error,
				}
			}

			return {
				data: response.data || [],
				error: null,
			}
		} catch (error) {
			console.error('OsobaService.getByPlacowkaId error:', error)
			return {
				data: [],
				error: error.message || 'Failed to fetch osoby by placowka ID',
			}
		}
	}

	async getById(osobaId) {
		try {
			if (!osobaId) {
				return {
					data: null,
					error: 'Invalid osoba ID provided',
				}
			}

			const url = `${this.endpoints.osobaDetails}/${encodeURIComponent(osobaId)}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: null,
					error: response.error,
				}
			}

			return {
				data: response.data || null,
				error: null,
			}
		} catch (error) {
			console.error('OsobaService.getById error:', error)
			return {
				data: null,
				error: error.message || 'Failed to fetch osoba details',
			}
		}
	}

	async search(searchTerm, options = {}) {
		try {
			if (!searchTerm) {
				return {
					data: [],
					error: 'Invalid search term provided',
					totalCount: 0,
					hasMore: false,
				}
			}

			const {
				limit = 50,
				sortColumn = '_id',
				sortDirection = 'desc',
			} = options

			const params = new URLSearchParams({
				sortcol: sortColumn,
				sortdir: sortDirection,
				limit: String(limit),
			})
			const url = `${this.endpoints.osobaSearch}?searchText=${encodeURIComponent(searchTerm)}&${params.toString()}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: [],
					error: response.error,
					totalCount: 0,
					hasMore: false,
				}
			}

			const totalCount = response.headers?.['x-total-count']
				? parseInt(response.headers['x-total-count'], 10)
				: response.data?.length || 0

			return {
				data: response.data || [],
				error: null,
				totalCount,
				hasMore: (response.data?.length || 0) < totalCount,
				headers: response.headers,
			}
		} catch (error) {
			console.error('OsobaService.search error:', error)
			return {
				data: [],
				error: error.message || 'Failed to search osoby',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async create(osobaData) {
		try {
			const response = await Axhelper.sendPostAsync(this.endpoints.osobaCreate, osobaData)

			if (response.error) {
				return {
					success: false,
					error: response.error,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('OsobaService.create error:', error)
			return {
				success: false,
				error: error.message || 'Failed to create osoba',
			}
		}
	}

	async update(osobaId, osobaData) {
		try {
			if (!osobaId) {
				return {
					success: false,
					error: 'Invalid osoba ID provided',
				}
			}

			const url = `${this.endpoints.osobaUpdate}/${encodeURIComponent(osobaId)}`
			const response = await Axhelper.sendPutAsync(url, osobaData)

			if (response.error) {
				return {
					success: false,
					error: response.error,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('OsobaService.update error:', error)
			return {
				success: false,
				error: error.message || 'Failed to update osoba',
			}
		}
	}

	async delete(osobaId) {
		try {
			if (!osobaId) {
				return {
					success: false,
					error: 'Invalid osoba ID provided',
					message: null,
				}
			}

			const url = `${this.endpoints.osobaDelete}/${encodeURIComponent(osobaId)}`
			const response = await Axhelper.sendDeleteAsync(url)

			if (response.error) {
				return {
					success: false,
					error: response.error,
					message: null,
				}
			}

			return {
				success: true,
				error: null,
				message: response.data || 'Osoba deleted successfully',
			}
		} catch (error) {
			console.error('OsobaService.delete error:', error)
			return {
				success: false,
				error: error.message || 'Failed to delete osoba',
				message: null,
			}
		}
	}
}

export default new OsobaService()
